import React, { useState, useEffect, useRef } from "react";
import {
  Typography,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Menu,
  MenuItem,
  Alert,
  ListItemIcon,
  TextField,
} from "@mhome/ui";
import { CircularProgress, LinearProgress } from "@mui/material";
import { useRecoilState, useRecoilValue } from "recoil";
import { platformState, activeScopeState } from "../global-state";
import { useAppInterface } from "../app/app-interface-provider";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ClearIcon from "@mui/icons-material/Clear";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import BugReportIcon from "@mui/icons-material/BugReport";
import LoadingSpinner from "../common/loading";
import { useToast } from "../common/toast";
import { Button } from "@mhome/ui";
import { useWebSocket } from "../ws/websocket";
import { spacingToPx } from "../lib/utils";
import {
  getHubList,
  removeHub,
  restartHub,
  getSetupStatus,
  cancelSetup,
  clearHubCache,
  diagnoseHub,
} from "../service/hub-service";
import { useComponentBackgroundColor } from "../common/adaptive-theme-provider";

const HubSetting = () => {
  const getComponentBgColor = useComponentBackgroundColor();
  const [loading, setLoading] = useState(true);
  const platform = useRecoilValue(platformState);
  const [activeScope] = useRecoilState(activeScopeState);
  const appInterface = useAppInterface();
  const isApp = platform?.source === "app";
  const isDesktop = platform?.source === "desktop";
  const { successToast, errorToast } = useToast();
  const [hubs, setHubs] = useState([]);
  const [hubCandidates, setHubCandidates] = useState([]);
  const [setupStatus, setSetupStatus] = useState(null);
  const { sendAndWaitWs } = useWebSocket();
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [restartDialogOpen, setRestartDialogOpen] = useState(false);
  const [clearCacheDialogOpen, setClearCacheDialogOpen] = useState(false);
  const [diagnoseDialogOpen, setDiagnoseDialogOpen] = useState(false);
  const [hubToRemove, setHubToRemove] = useState(null);
  const [hubToRestart, setHubToRestart] = useState(null);
  const [hubToClearCache, setHubToClearCache] = useState(null);
  const [hubToDiagnose, setHubToDiagnose] = useState(null);
  const [diagnoseResult, setDiagnoseResult] = useState(null);
  const [diagnosing, setDiagnosing] = useState(false);
  const [forceRemoveConfirmText, setForceRemoveConfirmText] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedHub, setSelectedHub] = useState(null);
  const [initDialogOpen, setInitDialogOpen] = useState(false);
  const [hubSetupStatus, setHubSetupStatus] = useState(null);
  const pollingIntervalRef = useRef(null);
  const [onDeviceHubInstance, setOnDeviceHubInstance] = useState(null);

  // Fetch hub candidates from app interface
  const fetchHubCandidates = async () => {
    if (!activeScope || !appInterface.isAvailable()) {
      setHubCandidates([]);
      return;
    }

    try {
      const result = await appInterface.getHubCandidates(activeScope);
      setHubCandidates(result?.candidates || []);
    } catch (error) {
      console.error("Error fetching hub candidates:", error);
      setHubCandidates([]);
    }
  };

  // Fetch setup status from API
  const fetchSetupStatus = async () => {
    if (!activeScope) {
      setSetupStatus(null);
      return;
    }

    try {
      const result = await getSetupStatus(sendAndWaitWs);
      if (result && !result.error) {
        setSetupStatus(result);
      } else {
        setSetupStatus(null);
      }
    } catch (error) {
      console.error("Error fetching setup status:", error);
      setSetupStatus(null);
    }
  };

  // Fetch on-device hub instance
  const fetchOnDeviceHubInstance = async () => {
    if (!activeScope || !appInterface.isAvailable()) {
      setOnDeviceHubInstance(null);
      return;
    }

    try {
      const result = await appInterface.getOnDeviceHubInstance(activeScope);
      if (result && !result.error) {
        setOnDeviceHubInstance(result);
      } else {
        setOnDeviceHubInstance(null);
      }
    } catch (error) {
      console.error("Error fetching on-device hub instance:", error);
      setOnDeviceHubInstance(null);
    }
  };

  useEffect(() => {
    if (activeScope) {
      fetchHubCandidates();
      fetchSetupStatus();
      fetchOnDeviceHubInstance();
      handleHubListFetch();
    } else {
      // For web demo, show mock data
      if (process.env.NODE_ENV !== "production") {
        // Set mock candidates for web display
        setHubCandidates([
          {
            deviceId: "demo-device-1",
            deviceName: "Demo Android Device",
            hubType: "Android",
          },
          {
            deviceId: "demo-device-2",
            deviceName: "Demo Desktop Device",
            hubType: "Desktop",
          },
        ]);
        // Set mock setup status to show "Hub Setup In Progress" section
        // Uncomment the following lines to show setup status:
        // setSetupStatus({
        //   deviceId: "demo-device-1",
        //   deviceName: "Demo Android Device",
        //   hubType: "Android",
        //   status: "s2",
        // });
        // Set mock on-device hub instance to show "Current Device" chip
        setOnDeviceHubInstance({
          hubId: "demo-hub-1",
          hubName: "Demo Hub 1",
        });
        setLoading(false);
        handleHubListFetch();
      }
    }
  }, [activeScope]);

  const handleMenuOpen = (event, hub, isSetupHub = false) => {
    setAnchorEl(event.currentTarget);
    setSelectedHub({ ...hub, inProgress: isSetupHub });
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedHub(null);
  };

  const handleRemoveClick = (hub) => {
    setHubToRemove(hub);
    setRemoveDialogOpen(true);
    handleMenuClose();
  };

  const handleRestartClick = (hub) => {
    setHubToRestart(hub);
    setRestartDialogOpen(true);
    handleMenuClose();
  };

  const handleRestartConfirm = async () => {
    if (!hubToRestart?.hubId) {
      errorToast("Hub ID not available");
      return;
    }

    try {
      setLoading(true);
      const response = await restartHub(sendAndWaitWs, hubToRestart.hubId);

      if (response && !response.error) {
        successToast("Hub restarting...");
        // Refresh hub list after a short delay
        setTimeout(() => {
          handleHubListFetch();
        }, 2000);
      } else {
        errorToast("Failed to restart hub");
      }
    } catch (error) {
      console.error("Error restarting hub:", error);
      errorToast("Failed to restart hub");
    } finally {
      setLoading(false);
      setHubToRestart(null);
      setRestartDialogOpen(false);
    }
  };

  const handleClearCacheClick = (hub) => {
    setHubToClearCache(hub);
    setClearCacheDialogOpen(true);
    handleMenuClose();
  };

  const handleClearCacheConfirm = async () => {
    if (!hubToClearCache?.hubId) {
      errorToast("Hub ID not available");
      return;
    }

    try {
      setLoading(true);
      const response = await clearHubCache(
        sendAndWaitWs,
        hubToClearCache.hubId
      );

      if (response && !response.error) {
        successToast("Hub cache cleared successfully");
        handleHubListFetch();
      } else {
        errorToast("Failed to clear hub cache");
      }
    } catch (error) {
      console.error("Error clearing hub cache:", error);
      errorToast("Failed to clear hub cache");
    } finally {
      setLoading(false);
      setHubToClearCache(null);
      setClearCacheDialogOpen(false);
    }
  };

  const handleCancelSetupClick = async () => {
    handleMenuClose();

    if (!activeScope) {
      errorToast("No active scope found");
      return;
    }

    try {
      setLoading(true);
      const response = await cancelSetup(sendAndWaitWs);

      if (response && response.success) {
        successToast("Hub setup cancelled");
        // Stop polling if dialog is open
        stopPollingHubSetupStatus();
        setInitDialogOpen(false);
        setHubSetupStatus(null);
        // Refresh candidates, status and list
        setTimeout(async () => {
          await fetchHubCandidates();
          await fetchSetupStatus();
          await handleHubListFetch();
        }, 1000);
      } else {
        errorToast("Failed to cancel hub setup");
      }
    } catch (error) {
      console.error("Error cancelling hub setup:", error);
      errorToast("Failed to cancel hub setup");
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveConfirm = async (force = false) => {
    if (!hubToRemove) return;

    try {
      setLoading(true);
      const response = await removeHub(sendAndWaitWs, hubToRemove.hubId, force);

      if (response && !response.error) {
        successToast(force ? "Hub force removed" : "Hub removed successfully");
        handleHubListFetch();
      } else {
        errorToast("Failed to remove hub");
      }
    } catch (error) {
      console.error("Error removing hub:", error);
      errorToast("Failed to remove hub");
    } finally {
      setLoading(false);
      setHubToRemove(null);
      setRemoveDialogOpen(false);
      setForceRemoveConfirmText("");
    }
  };

  const handleRemoveDialogClose = () => {
    setRemoveDialogOpen(false);
    setHubToRemove(null);
    setForceRemoveConfirmText("");
  };

  const handleRestartDialogClose = () => {
    setRestartDialogOpen(false);
    setHubToRestart(null);
  };

  const handleClearCacheDialogClose = () => {
    setClearCacheDialogOpen(false);
    setHubToClearCache(null);
  };

  const handleDiagnoseClick = (hub) => {
    setHubToDiagnose(hub);
    setDiagnoseResult(null);
    setDiagnoseDialogOpen(true);
    handleMenuClose();
    // Start diagnosis immediately
    handleDiagnoseHub(hub);
  };

  const handleDiagnoseHub = async (hub) => {
    if (!hub?.hubId) {
      errorToast("Hub ID not available");
      return;
    }

    try {
      setDiagnosing(true);
      setDiagnoseResult(null);

      // Check if this is the on-device hub
      const isOnDevice = isCurrentDeviceHub(hub);

      let response;
      if (isOnDevice) {
        // Call app interface method for on-device hub
        response = await appInterface.diagnoseOnDeviceHub(activeScope);
      } else {
        // Call websocket API for remote hub
        response = await diagnoseHub(sendAndWaitWs, hub.hubId);
      }

      if (response && !response.error) {
        setDiagnoseResult(response);
        successToast("Diagnosis completed");
      } else {
        setDiagnoseResult({
          error: response.error || "Failed to diagnose hub",
        });
        errorToast("Failed to diagnose hub");
      }
    } catch (error) {
      console.error("Error diagnosing hub:", error);
      setDiagnoseResult({ error: error.message || "Failed to diagnose hub" });
      errorToast("Failed to diagnose hub");
    } finally {
      setDiagnosing(false);
    }
  };

  const handleDiagnoseDialogClose = () => {
    setDiagnoseDialogOpen(false);
    setHubToDiagnose(null);
    setDiagnoseResult(null);
    setDiagnosing(false);
  };

  const handleHubListFetch = async () => {
    if (!activeScope) {
      // For web demo, use mock data
      if (process.env.NODE_ENV !== "production") {
        setHubs([
          {
            hubId: "demo-hub-1",
            hubName: "Main Hub - Living Room",
            status: "active",
            connectStatus: "active",
            firmwareVersion: "2.1.0",
          },
          {
            hubId: "demo-hub-2",
            hubName: "Secondary Hub - Bedroom",
            status: "ready",
            connectStatus: "active",
            firmwareVersion: "2.0.5",
          },
          {
            hubId: "demo-hub-3",
            hubName: "Office Hub",
            status: "ready",
            connectStatus: "inactive",
            firmwareVersion: "1.9.8",
          },
        ]);
        setLoading(false);
      }
      return;
    }
    setLoading(true);
    try {
      const response = await getHubList(sendAndWaitWs);

      if (Array.isArray(response)) {
        setHubs(response);
      } else if (response?.error) {
        setHubs([]);
        errorToast("Error getting hubs");
      } else {
        setHubs([]);
      }
    } catch (error) {
      console.error("Error getting hubs:", error);
      errorToast("Error getting hubs");
      setHubs([]);
    } finally {
      setLoading(false);
    }
  };

  const pollHubSetupStatus = async () => {
    if (!activeScope) {
      return;
    }

    try {
      const status = await getSetupStatus(sendAndWaitWs);

      if (status && !status.error) {
        setHubSetupStatus(status);

        // Stop polling if no deviceId (completed or cancelled)
        if (!status.deviceId) {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }

          // Refresh hub list and candidates
          setTimeout(async () => {
            await fetchHubCandidates();
            await fetchSetupStatus();
            await handleHubListFetch();
          }, 1000);
        }
      }
    } catch (error) {
      console.error("Error polling hub setup status:", error);
    }
  };

  const startPollingHubSetupStatus = () => {
    // Clear any existing polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    // Poll immediately
    pollHubSetupStatus();

    // Then poll every 2 seconds
    pollingIntervalRef.current = setInterval(() => {
      pollHubSetupStatus();
    }, 2000);
  };

  const stopPollingHubSetupStatus = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      stopPollingHubSetupStatus();
    };
  }, []);

  // Poll setup status when deviceId exists
  useEffect(() => {
    if (setupStatus?.deviceId && activeScope) {
      // If there's a deviceId, poll every 10 seconds
      const intervalId = setInterval(() => {
        fetchSetupStatus();
      }, 10000); // Poll every 10 seconds

      return () => {
        clearInterval(intervalId);
      };
    }
  }, [setupStatus?.deviceId, activeScope]);

  const handleRegisterAsHub = async (candidate) => {
    if (!activeScope) {
      errorToast("No active scope found");
      return;
    }

    try {
      setLoading(true);
      const result = await appInterface.initHub(activeScope);

      if (result?.success) {
        // Refresh setup status immediately after init
        await fetchSetupStatus();
        // Open dialog and start polling
        setHubSetupStatus({ deviceId: candidate.deviceId, status: "s1" });
        setInitDialogOpen(true);
        startPollingHubSetupStatus();
      } else {
        errorToast("Failed to initialize hub");
      }
    } catch (error) {
      console.error("Error initializing hub:", error);
      errorToast("Failed to initialize hub");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    if (status === "active") return "success";
    if (status === "ready") return "info";
    return "default";
  };

  const getStatusLabel = (status) => {
    if (status === "active") return "Active";
    if (status === "ready") return "Ready";
    if (status === "token") return "Token Assigned";
    return status || "Unknown";
  };

  const getInitStatusMessage = (status) => {
    switch (status) {
      case "s1":
        return "Step 1: Initializing...";
      case "s2":
        return "Step 2: Authenticating...";
      case "s3":
        return "Step 3: Syncing data...";
      default:
        return "Initializing...";
    }
  };

  const handleCloseInitDialog = () => {
    if (!hubSetupStatus?.deviceId) {
      setInitDialogOpen(false);
      setHubSetupStatus(null);
      stopPollingHubSetupStatus();
    }
  };

  // Helper function to check if hub is in setup progress
  const isHubInSetupProgress = () => {
    // If no setup status or deviceId is empty, no setup in progress
    return setupStatus && setupStatus.deviceId;
  };

  // Filter candidates that are already in setup
  const getAvailableCandidates = () => {
    if (!hubCandidates || hubCandidates.length === 0) return [];

    // If there's a setup in progress, filter out that candidate
    if (setupStatus && setupStatus.deviceId) {
      return hubCandidates.filter(
        (candidate) => candidate.deviceId !== setupStatus.deviceId
      );
    }

    return hubCandidates;
  };

  // Render a candidate card
  const renderCandidateCard = (candidate) => (
    <Card
      key={candidate.deviceId}
      style={{
        backgroundColor: getComponentBgColor("hsl(var(--card))"),
        boxShadow: "none",
      }}
    >
      <CardContent
        style={{ padding: spacingToPx(2), paddingBottom: spacingToPx(2) }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ flex: 1 }}>
            <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
              {candidate.deviceName || "Hub"}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              style={{ display: "block", marginTop: spacingToPx(0.5) }}
            >
              {candidate.hubType || "Unknown Type"}
            </Typography>
          </div>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleRegisterAsHub(candidate)}
            disabled={loading}
            style={{
              textTransform: "none",
              boxShadow: "none",
            }}
            size="small"
          >
            Setup
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  // Render a setup hub card
  const renderSetupHubCard = () => {
    if (!setupStatus || !setupStatus.deviceId) return null;

    return (
      <Card
        style={{
          backgroundColor: getComponentBgColor("hsl(var(--card))"),
          boxShadow: "none",
          border: "2px solid hsl(45 100% 51%)",
        }}
      >
        <CardContent
          style={{ padding: spacingToPx(2), paddingBottom: spacingToPx(2) }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <div style={{ flex: 1 }}>
              <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
                {setupStatus.deviceName || "Hub"}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                style={{ display: "block", marginTop: spacingToPx(0.5) }}
              >
                {setupStatus.hubType || "Unknown Type"}
              </Typography>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: spacingToPx(1),
                  marginTop: spacingToPx(0.5),
                }}
              >
                <Chip
                  label="Setting Up"
                  size="small"
                  color="warning"
                  style={{
                    height: "20px",
                  }}
                />
                <Chip
                  label={getInitStatusMessage(setupStatus.status)}
                  size="small"
                  color="info"
                  style={{
                    height: "20px",
                  }}
                />
              </div>
              <Alert severity="warning" style={{ marginTop: spacingToPx(1) }}>
                This device is being set up as a hub. Please wait for the setup
                to complete.
              </Alert>
            </div>
            <IconButton
              size="small"
              onClick={(e) => {
                setAnchorEl(e.currentTarget);
                setSelectedHub({ ...setupStatus, inProgress: true });
              }}
              style={{ marginLeft: spacingToPx(1) }}
            >
              <MoreVertIcon />
            </IconButton>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Helper function to check if hub is the current device
  const isCurrentDeviceHub = (hub) => {
    return (
      onDeviceHubInstance &&
      hub &&
      onDeviceHubInstance.hubId &&
      hub.hubId === onDeviceHubInstance.hubId
    );
  };

  // Render a hub card
  const renderHubCard = (hub, isSetupHub = false) => (
    <Card
      key={hub.hubId}
      style={{
        backgroundColor: getComponentBgColor("hsl(var(--card))"),
        boxShadow: "none",
        border: isSetupHub ? "2px solid hsl(45 100% 51%)" : "none",
      }}
    >
      <CardContent
        style={{ padding: spacingToPx(2), paddingBottom: spacingToPx(2) }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <div style={{ flex: 1 }}>
            <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
              {hub.hubName}
            </Typography>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: spacingToPx(1),
                marginTop: spacingToPx(0.5),
              }}
            >
              {isSetupHub ? (
                <>
                  <Chip
                    label="Setting Up"
                    size="small"
                    color="warning"
                    style={{
                      height: "20px",
                    }}
                  />
                  <Chip
                    label={getInitStatusMessage(hub.status)}
                    size="small"
                    color="info"
                    style={{
                      height: "20px",
                    }}
                  />
                </>
              ) : (
                <>
                  <Chip
                    label={getStatusLabel(hub.status)}
                    size="small"
                    color={getStatusColor(hub.status)}
                    style={{
                      height: "20px",
                    }}
                  />
                  {hub.connectStatus === "active" && (
                    <Chip
                      label="Connected"
                      size="small"
                      color="success"
                      style={{
                        height: "20px",
                      }}
                    />
                  )}
                  {isCurrentDeviceHub(hub) && (
                    <Chip
                      label="Current Device"
                      size="small"
                      color="primary"
                      style={{
                        height: "20px",
                      }}
                    />
                  )}
                </>
              )}
            </div>
            {hub.firmwareVersion && (
              <Typography
                variant="caption"
                color="text.secondary"
                style={{ display: "block" }}
              >
                Version: {hub.firmwareVersion}
              </Typography>
            )}
            {isSetupHub && (
              <Alert severity="warning" style={{ marginTop: spacingToPx(1) }}>
                This device is being set up as a hub. Please wait for the setup
                to complete.
              </Alert>
            )}
          </div>
          <IconButton
            size="small"
            onClick={(e) => handleMenuOpen(e, hub, isSetupHub)}
            style={{ marginLeft: spacingToPx(1) }}
          >
            <MoreVertIcon />
          </IconButton>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        padding: spacingToPx(2),
      }}
    >
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          {/* Hub Candidates Section - Only for app/desktop */}
          {(isDesktop || isApp) && getAvailableCandidates().length > 0 && (
            <div style={{ marginBottom: spacingToPx(2) }}>
              <Typography
                variant="subtitle2"
                style={{
                  fontWeight: 600,
                  marginBottom: spacingToPx(1),
                  paddingLeft: spacingToPx(1),
                  paddingRight: spacingToPx(1),
                }}
              >
                Available Devices for Hub Setup
              </Typography>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: spacingToPx(2),
                }}
              >
                {getAvailableCandidates().map((candidate) =>
                  renderCandidateCard(candidate)
                )}
              </div>
            </div>
          )}

          {/* Setup Hub Section */}
          {isHubInSetupProgress() && (
            <div style={{ marginBottom: spacingToPx(2) }}>
              <Typography
                variant="subtitle2"
                style={{
                  fontWeight: 600,
                  marginBottom: spacingToPx(1),
                  paddingLeft: spacingToPx(1),
                  paddingRight: spacingToPx(1),
                }}
              >
                Hub Setup In Progress
              </Typography>
              {renderSetupHubCard()}
            </div>
          )}

          {/* Cloud API Hubs Section */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: spacingToPx(2),
            }}
          >
            <Typography
              variant="subtitle2"
              style={{
                fontWeight: 600,
                marginBottom: spacingToPx(-1),
                paddingLeft: spacingToPx(1),
                paddingRight: spacingToPx(1),
              }}
            >
              Registered Hubs
            </Typography>
            {hubs.filter((hub) => hub.status !== "setup").length > 0 ? (
              hubs
                .filter((hub) => hub.status !== "setup")
                .map((hub) => renderHubCard(hub, false))
            ) : (
              <Card
                style={{
                  backgroundColor: getComponentBgColor("hsl(var(--card))"),
                  boxShadow: "none",
                }}
              >
                <CardContent
                  style={{
                    padding: spacingToPx(2),
                    paddingBottom: spacingToPx(2),
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      paddingTop: spacingToPx(2),
                      paddingBottom: spacingToPx(2),
                    }}
                  >
                    <Typography variant="body1" color="text.secondary">
                      No hubs found
                    </Typography>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </>
      )}

      {/* Menu for hub actions */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectedHub?.inProgress ? (
          <MenuItem onClick={handleCancelSetupClick}>
            <ListItemIcon>
              <ClearIcon fontSize="small" />
            </ListItemIcon>
            Cancel Setup
          </MenuItem>
        ) : (
          <>
            <MenuItem
              onClick={() => selectedHub && handleDiagnoseClick(selectedHub)}
            >
              <ListItemIcon>
                <BugReportIcon fontSize="small" />
              </ListItemIcon>
              Diagnose
            </MenuItem>
            <MenuItem
              onClick={() => selectedHub && handleRestartClick(selectedHub)}
              disabled={!selectedHub || selectedHub.connectStatus !== "active"}
            >
              <ListItemIcon>
                <RestartAltIcon fontSize="small" />
              </ListItemIcon>
              Restart
            </MenuItem>
            <MenuItem
              onClick={() => selectedHub && handleClearCacheClick(selectedHub)}
              disabled={!selectedHub || selectedHub.connectStatus !== "active"}
            >
              <ListItemIcon>
                <RemoveCircleOutlineIcon fontSize="small" />
              </ListItemIcon>
              Clear Cache
            </MenuItem>
            <MenuItem
              onClick={() => selectedHub && handleRemoveClick(selectedHub)}
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" className="text-destructive" />
              </ListItemIcon>
              Remove
            </MenuItem>
          </>
        )}
      </Menu>

      {/* Info Message Box */}
      <Card
        style={{
          backgroundColor: getComponentBgColor("hsl(var(--card))"),
          boxShadow: "none",
          marginTop: spacingToPx(2),
        }}
      >
        <CardContent>
          <div
            style={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <InfoOutlinedIcon
              className="text-primary"
              style={{
                marginRight: spacingToPx(2),
              }}
            />
            <div>
              <Typography variant="subtitle1" style={{ fontWeight: "bold" }}>
                What is a Hub?
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Hub is a master device running MeowCore in your local network.
                It hosts the engine to run automations locally. It also bridges
                your local environment to the MeowCore Cloud.
              </Typography>
              <Typography variant="subtitle1" style={{ fontWeight: "bold" }}>
                Supported hub devices:
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • MeowCore Android phone / tablet App
                <br />
                • MeowCore Windows / MacOS / Linux Desktop App (available soon)
                <br />• Dedicated MeowCore Hub device (available later)
                <br />• Deploy your own way with the source code (available
                soon)
              </Typography>
              <Typography variant="subtitle1" style={{ fontWeight: "bold" }}>
                Choose the right Hub for your home:
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Phone / Tablet App. Keep it charged and remain online on your
                local network 24/7. Remember to grant the necessary permissions
                to have MeowCore App always alive.
                <br />• Desktop App. Auto running MeowCore as long as the
                desktop app is alive.
              </Typography>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Remove Confirmation Dialog */}
      <Dialog
        open={removeDialogOpen}
        onClose={handleRemoveDialogClose}
        aria-labelledby="remove-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="remove-dialog-title">Remove Hub</DialogTitle>
        <DialogContent>
          <div
            style={{
              paddingTop: spacingToPx(2),
              paddingBottom: spacingToPx(2),
            }}
          >
            {/* Normal Remove Section */}
            <Typography
              variant="body1"
              style={{ marginBottom: spacingToPx(2) }}
            >
              Removing the hub will revoke hub access. Current data stored on
              the hub will be transferred to the cloud.
            </Typography>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: spacingToPx(3),
              }}
            >
              <Button
                onClick={() => handleRemoveConfirm(false)}
                color="primary"
                variant="contained"
                disabled={loading}
                size="large"
                style={{
                  textTransform: "none",
                  boxShadow: "none",
                  paddingLeft: spacingToPx(4),
                  paddingRight: spacingToPx(4),
                }}
              >
                Remove Hub and Back up Data to Cloud
                {loading && (
                  <CircularProgress
                    size={16}
                    style={{ marginLeft: spacingToPx(1) }}
                  />
                )}
              </Button>
            </div>

            {/* Divider */}
            <div
              style={{
                borderBottom: "1px solid hsl(var(--border))",
                marginTop: spacingToPx(3),
                marginBottom: spacingToPx(3),
              }}
            />

            {/* Force Remove Section */}
            <Alert severity="warning" style={{ marginBottom: spacingToPx(2) }}>
              <Typography
                variant="body2"
                style={{ marginBottom: spacingToPx(1) }}
              >
                <strong>What if you cannot access the hub device?</strong>
              </Typography>
              <Typography variant="body2">
                If the hub is no longer reachable from the cloud, data cannot be
                backed up to the cloud. You can force remove it, but all data
                from the hub will be lost.
              </Typography>
            </Alert>

            <Typography
              variant="body2"
              style={{ marginBottom: spacingToPx(1) }}
            >
              Type <strong>confirm</strong> to enable force removal:
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={forceRemoveConfirmText}
              onChange={(e) => setForceRemoveConfirmText(e.target.value)}
              placeholder="Type 'confirm' here"
              style={{ marginBottom: spacingToPx(2) }}
            />

            <div style={{ display: "flex", justifyContent: "center" }}>
              <Button
                onClick={() => handleRemoveConfirm(true)}
                color="error"
                variant="contained"
                disabled={
                  loading || forceRemoveConfirmText.toLowerCase() !== "confirm"
                }
                size="large"
                style={{
                  textTransform: "none",
                  boxShadow: "none",
                  paddingLeft: spacingToPx(4),
                  paddingRight: spacingToPx(4),
                }}
              >
                Remove Hub and Hub Data is Lost
                {loading && (
                  <CircularProgress
                    size={16}
                    style={{ marginLeft: spacingToPx(1) }}
                  />
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Restart Confirmation Dialog */}
      <Dialog
        open={restartDialogOpen}
        onClose={handleRestartDialogClose}
        aria-labelledby="restart-dialog-title"
      >
        <DialogTitle id="restart-dialog-title">Restart Hub</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to restart this hub?</Typography>
        </DialogContent>
        <DialogActions
          style={{
            justifyContent: "space-between",
            paddingLeft: spacingToPx(2),
            paddingRight: spacingToPx(2),
            paddingBottom: spacingToPx(2),
          }}
        >
          <Button
            onClick={handleRestartDialogClose}
            variant="outlined"
            style={{
              textTransform: "none",
              boxShadow: "none",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRestartConfirm}
            color="primary"
            variant="contained"
            disabled={loading}
            style={{
              textTransform: "none",
              boxShadow: "none",
            }}
          >
            Restart
            {loading && (
              <CircularProgress
                size={16}
                style={{ marginLeft: spacingToPx(1) }}
              />
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear Cache Confirmation Dialog */}
      <Dialog
        open={clearCacheDialogOpen}
        onClose={handleClearCacheDialogClose}
        aria-labelledby="clear-cache-dialog-title"
      >
        <DialogTitle id="clear-cache-dialog-title">Clear Hub Cache</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to clear the cache for this hub?
          </Typography>
        </DialogContent>
        <DialogActions
          style={{
            justifyContent: "space-between",
            paddingLeft: spacingToPx(2),
            paddingRight: spacingToPx(2),
            paddingBottom: spacingToPx(2),
          }}
        >
          <Button
            onClick={handleClearCacheDialogClose}
            variant="outlined"
            style={{
              textTransform: "none",
              boxShadow: "none",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleClearCacheConfirm}
            color="primary"
            variant="contained"
            disabled={loading}
            style={{
              textTransform: "none",
              boxShadow: "none",
            }}
          >
            Clear Cache
            {loading && (
              <CircularProgress
                size={16}
                style={{ marginLeft: spacingToPx(1) }}
              />
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hub Initialization Status Dialog */}
      <Dialog
        open={initDialogOpen}
        onClose={handleCloseInitDialog}
        aria-labelledby="init-dialog-title"
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={!!hubSetupStatus?.deviceId}
      >
        <DialogTitle id="init-dialog-title">Hub Initialization</DialogTitle>
        <DialogContent>
          <div
            style={{
              paddingTop: spacingToPx(2),
              paddingBottom: spacingToPx(2),
            }}
          >
            {hubSetupStatus?.deviceId && (
              <Alert
                severity="warning"
                style={{ marginBottom: spacingToPx(2) }}
              >
                Please do not close this dialog until the initialization is
                complete.
              </Alert>
            )}

            <Typography
              variant="body1"
              style={{ marginBottom: spacingToPx(2) }}
            >
              {getInitStatusMessage(hubSetupStatus?.status)}
            </Typography>

            {!hubSetupStatus?.deviceId && (
              <Alert
                severity="success"
                style={{ marginBottom: spacingToPx(2) }}
              >
                Your device has been successfully registered as a hub!
              </Alert>
            )}

            {hubSetupStatus?.deviceId && (
              <div style={{ marginTop: spacingToPx(2) }}>
                <LinearProgress
                  variant="indeterminate"
                  style={{ height: 8, borderRadius: 4 }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  style={{
                    display: "block",
                    marginTop: spacingToPx(1),
                    textAlign: "center",
                  }}
                >
                  Setting up...
                </Typography>
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseInitDialog}
            disabled={!!hubSetupStatus?.deviceId}
            variant="contained"
          >
            {hubSetupStatus?.deviceId ? "Please Wait..." : "Close"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hub Diagnose Dialog */}
      <Dialog
        open={diagnoseDialogOpen}
        onClose={handleDiagnoseDialogClose}
        aria-labelledby="diagnose-dialog-title"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle id="diagnose-dialog-title">
          Hub Diagnosis - {hubToDiagnose?.hubName}
          {isCurrentDeviceHub(hubToDiagnose) && (
            <Chip
              label="Current Device"
              size="small"
              color="primary"
              style={{ marginLeft: spacingToPx(1), height: "20px" }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          <div
            style={{
              paddingTop: spacingToPx(2),
              paddingBottom: spacingToPx(2),
            }}
          >
            {diagnosing ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  paddingTop: spacingToPx(4),
                  paddingBottom: spacingToPx(4),
                }}
              >
                <CircularProgress
                  size={48}
                  style={{ marginBottom: spacingToPx(2) }}
                />
                <Typography variant="body1" color="text.secondary">
                  Running diagnostics...
                </Typography>
              </div>
            ) : diagnoseResult ? (
              <div>
                {diagnoseResult.error ? (
                  <Alert
                    severity="error"
                    style={{ marginBottom: spacingToPx(2) }}
                  >
                    <Typography variant="body2">
                      <strong>Error:</strong> {diagnoseResult.error}
                    </Typography>
                  </Alert>
                ) : (
                  <div>
                    <Alert
                      severity="success"
                      style={{ marginBottom: spacingToPx(2) }}
                    >
                      Diagnosis completed successfully
                    </Alert>
                    <Card
                      style={{
                        backgroundColor:
                          getComponentBgColor("hsl(var(--card))"),
                        boxShadow: "none",
                        border: "1px solid hsl(var(--border))",
                      }}
                    >
                      <CardContent>
                        <Typography
                          variant="subtitle2"
                          style={{
                            fontWeight: 600,
                            marginBottom: spacingToPx(1),
                          }}
                        >
                          Diagnostic Results:
                        </Typography>
                        <pre
                          className="hover:bg-accent"
                          style={{
                            padding: spacingToPx(2),
                            borderRadius: spacingToPx(1),
                            overflow: "auto",
                            maxHeight: "400px",
                            fontSize: "0.875rem",
                            fontFamily: "monospace",
                            margin: 0,
                          }}
                        >
                          {JSON.stringify(diagnoseResult, null, 2)}
                        </pre>
                      </CardContent>
                    </Card>
                  </div>
                )}
              </div>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No diagnostic results available.
              </Typography>
            )}
          </div>
        </DialogContent>
        <DialogActions
          style={{
            justifyContent: "space-between",
            paddingLeft: spacingToPx(2),
            paddingRight: spacingToPx(2),
            paddingBottom: spacingToPx(2),
          }}
        >
          <Button
            onClick={handleDiagnoseDialogClose}
            variant="outlined"
            style={{
              textTransform: "none",
              boxShadow: "none",
            }}
          >
            Close
          </Button>
          <Button
            onClick={() => hubToDiagnose && handleDiagnoseHub(hubToDiagnose)}
            color="primary"
            variant="contained"
            disabled={diagnosing}
            style={{
              textTransform: "none",
              boxShadow: "none",
            }}
          >
            Run Again
            {diagnosing && (
              <CircularProgress
                size={16}
                style={{ marginLeft: spacingToPx(1) }}
              />
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default HubSetting;
